<template>
  <div
    ref="container"
    class="oliver-avatar"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- Thinking rings -->
    <template v-if="thinking">
      <div
        v-for="i in 3"
        :key="i"
        class="oliver-avatar__ring"
        :style="{
          width: `${88 + (i - 1) * 22}px`,
          height: `${88 + (i - 1) * 22}px`,
          '--ring-delay': `${(i - 1) * 0.35}s`,
        }"
      />
    </template>

    <!-- Idle glow -->
    <div v-else class="oliver-avatar__glow" />

    <!-- Main orb -->
    <div class="oliver-avatar__body" :style="{ transform: bodyTransform }">
      <div class="oliver-avatar__sheen" />

      <!-- Eyes -->
      <div class="oliver-avatar__eyes" :style="eyesStyle">
        <div v-for="n in 2" :key="n" class="oliver-avatar__eye">
          <div class="oliver-avatar__eye-shine" />
        </div>
      </div>

      <!-- Mouth -->
      <div class="oliver-avatar__mouth">
        <div v-if="thinking" class="oliver-avatar__dots">
          <div
            v-for="i in 3"
            :key="i"
            class="oliver-avatar__dot"
            :style="{ '--dot-delay': `${(i - 1) * 0.18}s` }"
          />
        </div>
        <svg v-else width="22" height="10" viewBox="0 0 22 10">
          <path
            :d="hovered ? 'M2 3 Q11 12 20 3' : 'M2 2 Q11 10 20 2'"
            fill="none"
            stroke-width="2.2"
            stroke-linecap="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
const STIFFNESS = 60;
const DAMPING = 18;
const DT = 1 / 60;
const THINK_OMEGA = (2 * Math.PI) / 1.2;

export default {
  name: "OliverAvatar",
  props: {
    thinking: { type: Boolean, default: false },
  },
  data() {
    return {
      hovered: false,
      springX: 0,
      springY: 0,
      velX: 0,
      velY: 0,
      targetX: 0,
      targetY: 0,
      thinkPhase: 0,
      rafId: null,
    };
  },
  computed: {
    eyeOffsetX() {
      return this.springX * 4;
    },
    eyeOffsetY() {
      return this.springY * 3;
    },
    tiltX() {
      return this.springY * -4;
    },
    tiltY() {
      return this.springX * 4;
    },
    bodyScale() {
      if (this.thinking) return 1 + 0.025 * Math.sin(this.thinkPhase);
      return this.hovered ? 1.07 : 1;
    },
    bodyTransform() {
      return `perspective(300px) rotateX(${this.tiltX}deg) rotateY(${this.tiltY}deg) scale(${this.bodyScale})`;
    },
    eyesStyle() {
      return {
        transform: `translate(calc(-50% + ${this.eyeOffsetX}px), ${this.eyeOffsetY}px)`,
      };
    },
  },
  mounted() {
    this._onMouseMove = this.onMouseMove.bind(this);
    this._tick = this.tick.bind(this);
    window.addEventListener("mousemove", this._onMouseMove);
    this.rafId = requestAnimationFrame(this._tick);
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this._onMouseMove);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  methods: {
    onMouseMove(e) {
      const el = this.$refs.container;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (window.innerWidth / 2);
      const dy = (e.clientY - cy) / (window.innerHeight / 2);
      this.targetX = Math.max(-1, Math.min(1, dx));
      this.targetY = Math.max(-1, Math.min(1, dy));
    },
    tick() {
      const ax =
        -STIFFNESS * (this.springX - this.targetX) - DAMPING * this.velX;
      const ay =
        -STIFFNESS * (this.springY - this.targetY) - DAMPING * this.velY;
      this.velX += ax * DT;
      this.velY += ay * DT;
      this.springX += this.velX * DT;
      this.springY += this.velY * DT;

      if (this.thinking) {
        this.thinkPhase = (this.thinkPhase + THINK_OMEGA * DT) % (2 * Math.PI);
      }

      this.rafId = requestAnimationFrame(this._tick);
    },
  },
};
</script>

<style lang="scss" scoped>
.oliver-avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  user-select: none;
  flex-shrink: 0;
}

.oliver-avatar__ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid var(--oliver-accent);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ring-pulse 2s ease-in-out infinite;
  animation-delay: var(--ring-delay, 0s);
}

@keyframes ring-pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.5;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.08);
    opacity: 0.15;
  }
}

.oliver-avatar__glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, var(--oliver-accent) 0%, transparent 70%);
  opacity: 0.35;
  animation: glow-pulse 3.5s ease-in-out infinite;
  pointer-events: none;
}

@keyframes glow-pulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.35;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.55;
  }
}

.oliver-avatar__body {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  position: relative;
  will-change: transform;
  background: radial-gradient(
    circle at 38% 35%,
    #fff 0%,
    var(--oliver-accent) 100%
  );
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12),
    inset 0 -4px 10px rgba(0, 0, 0, 0.08);
}

.oliver-avatar__sheen {
  position: absolute;
  top: 12%;
  left: 18%;
  width: 32%;
  height: 22%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  filter: blur(4px);
  transform: rotate(-20deg);
  pointer-events: none;
}

.oliver-avatar__eyes {
  position: absolute;
  top: 36%;
  left: 50%;
  display: flex;
  gap: 7px;
}

.oliver-avatar__eye {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  position: relative;
  flex-shrink: 0;
  background: var(--oliver-accent);
  filter: brightness(0.55);
  box-shadow: 0 0 3px var(--oliver-accent);
}

.oliver-avatar__eye-shine {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 2.5px;
  height: 2.5px;
  border-radius: 50%;
  background: white;
  opacity: 0.9;
}

.oliver-avatar__mouth {
  position: absolute;
  bottom: 22%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.oliver-avatar__dots {
  display: flex;
  gap: 4px;
}

.oliver-avatar__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--oliver-accent);
  filter: brightness(0.55);
  opacity: 0.8;
  animation: dot-bounce 0.7s ease-in-out infinite;
  animation-delay: var(--dot-delay, 0s);
}

@keyframes dot-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

path {
  stroke: var(--oliver-accent);
  filter: brightness(0.55);
}
</style>
